/**
 * Legacy RTL strike profile on the left edge of the Real-Time GEX chart.
 * Shows buying vs selling power (volume / delta / premium) at each strike.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import type { IChartApi, ISeriesApi } from 'lightweight-charts';
import type { RtlLegacyProfileLevel, RtlLegacyProfileMetric } from '../../utils/rtlLegacyStrikeProfile';
import { useChartTheme } from '../../theme/chartTheme';

const VP_WIDTH = 80;
const MONO_FONT = '"IBM Plex Mono", ui-monospace, monospace';

export interface RtlLegacyVolumeProfileOverlayProps {
  chart: IChartApi | null;
  series: ISeriesApi<'Candlestick'> | null;
  levels: RtlLegacyProfileLevel[];
  metric: RtlLegacyProfileMetric;
  enabled: boolean;
  underlyingLabel?: string;
}

const METRIC_LABEL: Record<RtlLegacyProfileMetric, string> = {
  volume: 'RTL Vol',
  delta: 'RTL Δ',
  premium: 'RTL $',
};

const RtlLegacyVolumeProfileOverlay: React.FC<RtlLegacyVolumeProfileOverlayProps> = ({
  chart,
  series,
  levels,
  metric,
  enabled,
  underlyingLabel,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const theme = useChartTheme();

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || !chart || !series || !enabled) return;

    const plotW = chart.timeScale().width();
    const h = wrap.clientHeight;
    if (plotW < 10 || h < 10) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = plotW * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${plotW}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, plotW, h);

    if (!levels.length) return;

    ctx.globalAlpha = 0.62;
    ctx.fillStyle = theme.background;
    ctx.fillRect(0, 0, VP_WIDTH, h);
    ctx.globalAlpha = 1;

    let maxMag = 0;
    for (const lv of levels) {
      maxMag = Math.max(maxMag, lv.magnitude, lv.boughtPower, lv.soldPower);
    }
    if (maxMag <= 0) maxMag = 1;

    for (const lv of levels) {
      const y = series.priceToCoordinate(lv.chartPrice);
      if (y == null || y < 4 || y > h - 4) continue;

      const barH = Math.max(2, Math.min(12, h / Math.max(levels.length, 16)));
      const innerW = VP_WIDTH - 12;
      const buyW = (lv.boughtPower / maxMag) * (innerW * 0.45);
      const sellW = (lv.soldPower / maxMag) * (innerW * 0.45);
      const midX = VP_WIDTH / 2;

      ctx.globalAlpha = 0.55;
      ctx.fillStyle = theme.up;
      ctx.fillRect(midX - buyW, y - barH / 2, buyW, barH);
      ctx.fillStyle = theme.down;
      ctx.fillRect(midX, y - barH / 2, sellW, barH);
      ctx.globalAlpha = 1;

      const netW = (Math.abs(lv.netValue) / maxMag) * (innerW * 0.35);
      let fill = theme.muted;
      let fillAlpha = 0.45;
      if (lv.direction === 'bullish') { fill = theme.up; fillAlpha = 0.88; }
      if (lv.direction === 'bearish') { fill = theme.down; fillAlpha = 0.9; }
      ctx.globalAlpha = fillAlpha;
      ctx.fillStyle = fill;
      if (lv.netValue >= 0) {
        ctx.fillRect(midX, y - barH / 2 - 1, netW, 2);
      } else {
        ctx.fillRect(midX - netW, y - barH / 2 - 1, netW, 2);
      }
      ctx.globalAlpha = 1;

      if (Math.abs(lv.netValue) / maxMag > 0.55) {
        ctx.font = `8px ${MONO_FONT}`;
        ctx.fillStyle = lv.direction === 'bullish' ? theme.up : lv.direction === 'bearish' ? theme.down : theme.text;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(lv.strike.toFixed(0)), VP_WIDTH + 2, y);
      }
    }

    ctx.font = `9px ${MONO_FONT}`;
    ctx.fillStyle = theme.accent;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(METRIC_LABEL[metric], 4, 6);
    if (underlyingLabel) {
      ctx.fillStyle = theme.text;
      ctx.fillText(underlyingLabel.slice(0, 10), 4, 18);
    }
    ctx.fillStyle = theme.up;
    ctx.fillText('buy', 4, h - 28);
    ctx.fillStyle = theme.down;
    ctx.fillText('sell', 4, h - 16);
  }, [chart, series, levels, enabled, metric, underlyingLabel, theme]);

  useEffect(() => {
    if (!chart || !enabled) return;
    const redraw = () => requestAnimationFrame(paint);
    redraw();
    chart.timeScale().subscribeVisibleLogicalRangeChange(redraw);
    const ro = new ResizeObserver(redraw);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(redraw);
      ro.disconnect();
    };
  }, [chart, enabled, paint]);

  useEffect(() => {
    paint();
  }, [paint, levels, metric]);

  if (!enabled || !levels.length) return null;

  const plotW = chart?.timeScale().width() ?? 0;

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: plotW > 0 ? plotW : '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 3,
        overflow: 'hidden',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

export default RtlLegacyVolumeProfileOverlay;
